function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function isIOS() {
  const ua = navigator.userAgent;
  const platform = navigator.platform;

  // iPhone / iPod
  if (/iPhone|iPod/i.test(ua)) {
    return true;
  }

  // iPad (newer iPadOS reports as MacIntel + touch)
  if (
    /iPad/i.test(ua) ||
    (platform === "MacIntel" && navigator.maxTouchPoints > 1)
  ) {
    return true;
  }

  // macOS
  if (/Mac/i.test(platform)) {
    return true;
  }

  return false;
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
      element.setAttribute(
        "form",
        `${element.form.getAttribute("id")}-${uniqueKey}`
      );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    let isCollapsible = true;
    if (
      summary.dataset.collapsible &&
      summary.dataset.collapsible === "false"
    ) {
      isCollapsible = false;
    }
    if (!isCollapsible) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) {
    return;
  }
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    ) {
      return;
    }

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== "TAB") {
      return;
    } // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement) {
        currentFocusedElement.classList.remove("focused");
      }

      if (mouseClick) {
        return;
      }

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) {
      model.modelViewerUI.pause();
    }
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) {
    elementToFocus.focus();
  }
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") {
    return;
  }

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) {
    return;
  }

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", {bubbles: true});
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function(...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") {
      return;
    }

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) {
      return;
    }

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
        event,
        this.mainDetailsToggle.querySelector("summary")
      )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) {
        event.preventDefault();
      }
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
            "transitionend",
            addTrapFocus
          );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) {
      return;
    }

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) {
      elementToFocus?.setAttribute("aria-expanded", false);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      ) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) {
      return;
    }
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") {
        this.hide();
      }
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        ) {
          this.hide();
        }
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) {
          this.hide();
        }
      });
    }
  }

  connectedCallback() {
    if (this.moved) {
      return;
    }
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) {
      popup.loadContent();
    }
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}

customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) {
        return;
      }
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) {
      return;
    }
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) {
        modal.show(button);
      }
    });
  }
}

customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) {
      return;
    }
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) {
        deferredElement.focus();
      }
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }

      if (deferredElement.classList.contains("js-vimeo")) {
        // Create a new Vimeo player instance
        const player = new Vimeo.Player(deferredElement);
        player.play().catch((error) => {
          console.error("Could not play:", error);
        });
      } else if (deferredElement.classList.contains("js-youtube")) {
        const player = new YT.Player(deferredElement.getAttribute("id"), {
          events: {
            onReady: () => {
              player.playVideo();
            },
          },
        });
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

// Populate Vimeo thumbnails without inline scripts
(function attachVimeoThumbPopulator() {
  function populateVimeoThumb(img) {
    if (!img || img.dataset.vimeoLoaded === "true") {
      return;
    }
    var id = img.getAttribute("data-vimeo-id");
    if (!id) {
      return;
    }
    img.dataset.vimeoLoaded = "true";
    var api =
      "https://vimeo.com/api/oembed.json?url=" +
      encodeURIComponent("https://vimeo.com/" + id);
    fetch(api)
      .then(function(r) {
        return r.json();
      })
      .then(function(d) {
        if (d && d.thumbnail_url) {
          img.src = d.thumbnail_url;
        }
      })
      .catch(function() {
      });
  }

  function scan() {
    document
      .querySelectorAll("img[data-vimeo-id]")
      ?.forEach(populateVimeoThumb);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scan);
  } else {
    scan();
  }

  document.addEventListener("shopify:section:load", scan);
  document.addEventListener("shopify:section:select", scan);
})();

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) {
      return;
    }

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) {
      return;
    }
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
      this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) {
      return;
    }

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) {
      return;
    }

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();

    window.addEventListener("resize", () => {
      this.scaleSlideshowText();
    });

    document.fonts?.ready.then(() => {
      this.scaleSlideshowText();
    });

    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) {
      return;
    }

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) {
      this.currentPage = 1;
    }

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true") {
          this.setAutoPlay();
        }
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          {once: true}
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true") {
      this.setAutoPlay();
    }
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) {
      clearTimeout(this.setPositionTimeout);
    }
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) {
      return;
    }

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) {
        return;
      }
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length) {
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        }
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length) {
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        }
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) {
      return;
    }

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) +
        1 -
        this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  scaleSlideshowText() {
    const slideEls = this.querySelectorAll(".slideshow__text");

    slideEls.forEach((slideEl) => {
      if (slideEl.dataset.textFitWindow !== "true") {
        return;
      }

      const textEl = slideEl.querySelector(".banner__heading");

      if (slideEl && textEl) {
        var computedStyle = getComputedStyle(slideEl);

        let slideElHeight = slideEl.clientHeight; // height with padding
        let slideElWidth = slideEl.clientWidth; // width with padding
        slideElHeight -=
          parseFloat(computedStyle.paddingTop) +
          parseFloat(computedStyle.paddingBottom);
        slideElWidth -=
          parseFloat(computedStyle.paddingLeft) +
          parseFloat(computedStyle.paddingRight);

        let textElHeight = textEl.clientHeight;
        let textElWidth = textEl.clientWidth;

        const scaleHeight = slideElHeight / textElHeight;
        const scaleWidth = slideElWidth / textElWidth;

        const scale = Math.min(scaleHeight, scaleWidth);

        const fontSize = parseFloat(
          getComputedStyle(textEl).getPropertyValue("font-size")
        );
        const newFontSize = Math.floor(fontSize * scale - 5);

        //  textEl.style.transform = `scale(${scale})`;
        textEl.style.fontSize = newFontSize + "px";
        textEl.style.opacity = "1";
      }
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({target}) {
    const {value, tagName} = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) {
        return;
      }
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) {
        selectedSwatchValue.innerHTML = value;
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({dataset}) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) {
          return;
        }
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      {rootMargin: "0px 0px 400px 0px"}
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({id, quantity});
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class BannerFont extends HTMLElement {
  constructor() {
    super();

    gsap.registerPlugin(SplitText)

    // this.hideBuyButton();

    this.stickyBottomBar =
      this.nextElementSibling ?? document.querySelector(".banner-font-sticky");

    this.updateStickyBottomBar();
  


//     let textEl = this.querySelector(".product-link .product-title");
//     let split = SplitText.create(textEl, { type: "chars" });
// //     gsap.from(split.chars, {
// //   duration: 1, 
// //   y: 100,       // animate from 100px below
// //   autoAlpha: 0, // fade in from opacity: 0 and visibility: hidden
// //   stagger: 0.05 // 0.05 seconds between each,
// //   repeat:-1
// // });


    window.addEventListener("resize", () => {

      // this.scaleBannerFont();

      this.updateStickyBottomBar();
    });

    document.fonts?.ready.then(() => this.scaleBannerFont());

    const observer = new IntersectionObserver(
      (elements) => {
        elements.forEach((element) => {
          if (this.stickyBottomBar) {
            if (element.isIntersecting) {
              this.stickyBottomBar.style.transform = "translateY(100%)";
            } else {
              this.stickyBottomBar.style.transform = "translateY(0)";
            }
          }
        });
      },
      {
        rootMargin: "0px 0px 0px 0px",
      }
    );
    observer.observe(this);

    // this.querySelector(".buy-button").addEventListener(
    //   "click",
    //   this.scrollToBuyForm.bind(this)
    // );

    // if (this.stickyBottomBar)
    //   this.stickyBottomBar
    //     .querySelector(".buy-button")
    //     .addEventListener("click", (e) => this.scrollToBuyForm(e, "instant"));

  }

  scaleBannerFont() {

    const slideEl = this.querySelector(".product-link");
    const textEl = this.querySelector(".product-link .product-title");
    let split = SplitText.create(textEl, {type: "chars"});


    if (slideEl && textEl) {
      if (window.innerWidth >= 640) {
        const isMultiWord = this.dataset.multiWord === "true";
        if (isMultiWord) {
          textEl.style.opacity = "1";
          return;
        }

        var computedStyle = getComputedStyle(slideEl);

        let slideElHeight = slideEl.clientHeight; // height with padding
        let slideElWidth = slideEl.clientWidth; // width with padding
        slideElHeight -=
          parseFloat(computedStyle.paddingTop) +
          parseFloat(computedStyle.paddingBottom);
        slideElWidth -=
          parseFloat(computedStyle.paddingLeft) +
          parseFloat(computedStyle.paddingRight);

        let textElHeight = textEl.clientHeight;
        let textElWidth = textEl.clientWidth;

        const scaleHeight = slideElHeight / textElHeight;
        const scaleWidth = slideElWidth / textElWidth;

        const scale = Math.min(scaleHeight, scaleWidth);

        const fontSize = parseFloat(
          getComputedStyle(textEl).getPropertyValue("font-size")
        );
        const newFontSize = Math.floor(fontSize * scale - 5);

        // textEl.style.transform = `scale(${scale})`;
        textEl.style.fontSize = newFontSize + "px";
        // textEl.style.opacity = "1";
      } else {
        // textEl.style.opacity = "1";
        let mobileSize = this.dataset.mobileSize;
        textEl.style.fontSize = mobileSize + "px";
      }


      if (this.dataset.typing == 'true') {
        let timeline = gsap.timeline({
          repeat: -1,
          repeatDelay: 1.5,
          onStart: function() {
            gsap.set(split.chars, {opacity: 0});
            textEl.style.opacity = "1";
          }
        });
        timeline.set(split.chars, {
          opacity: 0,
          delay: 0.5
        })
        timeline.fromTo(split.chars, {

          opacity: 0,       // animate from 100px below
        }, {opacity: 1, duration: 0.05, stagger: 0.25}, '+=0.5');
      } else {
        this.querySelector('.product-title').style.opacity = "1";
      }

    }
  }

  updateStickyBottomBar() {
    if (this.stickyBottomBar) {
      if (window.innerWidth <= 767) {
        const headerHeight = document.querySelector("header").offsetHeight;
        this.stickyBottomBar.style.bottom = headerHeight - 1 + "px";
      } else {
        this.stickyBottomBar.style.bottom = "0px";
      }
    }
  }

  scrollToBuyForm(e, behavior = "smooth") {
    const element = document.querySelector("product-info");
    if (!element) {
      return;
    }

    e.preventDefault();

    if (window.innerWidth > 767) {
      element.scrollIntoView({block: "start", behavior: behavior});

      // for ios
      const container = document.querySelector("body");
      const top = element.offsetTop - container.offsetTop;
      container.scrollTo({top, behavior});
    } else {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset + 75;

      window.scrollTo({
        top: offsetPosition,
        behavior: behavior,
      });

      // for ios
      const container = document.querySelector("body");
      const top = element.offsetTop - container.offsetTop + 75;
      container.scrollTo({top, behavior});
    }
  }

  
}

customElements.define("banner-font", BannerFont);

class FilterFontTypes extends HTMLElement {
  constructor() {
    super();

    this.parentEl = document.getElementById(this.dataset.parentId);

    // this.selectedFontTypes = ["All"];
    this.selectedFontTypes = [];
    this.total = this.querySelectorAll(".font-type").length;

    this.init();
    this.initSwipeable();
    this.initHideStickyAction();
    this.initActiveFontTypes();

    // this.updateStickyFiltersPosition();
    // window.addEventListener(
    //   "resize",
    //   this.updateStickyFiltersPosition.bind(this)
    // );
  }

  updateStickyFiltersPosition() {
    const actionContainer = document.querySelector(
      ".collection-product-grid-action-container"
    );
    console.log(window.innerWidth);
    if (window.innerWidth >= 768) {
      const header = document.querySelector("header");
      if (!header) {
        return;
      }
      console.log(header.offsetHeight);
      actionContainer.style.top = header.offsetHeight + "px";
    } else {
      const facetsWrapper = this.closest(".facets-wrapper");
      if (!facetsWrapper) {
        return;
      }

      console.log(facetsWrapper.offsetHeight);
      actionContainer.style.top = facetsWrapper.offsetHeight + "px";
    }
  }

  init() {
    this.querySelectorAll(".font-type").forEach((fontTypeEl) => {
      fontTypeEl.addEventListener("click", (e) => {
        // alert('hey');


        console.log('in animation start');
        gsap.to('.card-wrapper', {opacity: 0, scale: 0.8, duration: 0.25});


        let target = e.target;
        if (!target.classList.contains("font-type")) {
          target = target.closest(".font-type");
        }

        const fontType = target.dataset.fontType;

        if (fontType === "All") {
          this.selectedFontTypes = ["All"];
        } else {
          const indexAll = this.selectedFontTypes.indexOf("All");
          if (indexAll > -1) {
            this.selectedFontTypes.splice(indexAll, 1);
          }

          const index = this.selectedFontTypes.indexOf(fontType);
          if (index === -1) {
            // alert('not selected');
            // this.selectedFontTypes.push(fontType);
            this.selectedFontTypes = [fontType];
          } else {
            this.selectedFontTypes.splice(index, 1);
          }

          if (
            this.selectedFontTypes.length === this.total - 1 ||
            this.selectedFontTypes.length === 0
          ) {
            this.selectedFontTypes = ["All"];
          }
        }

        this.updateActiveFontTypes();

        this.updateShownProductList();

        window.dispatchEvent(new Event("card-product:scale-product-title"));
      });
    });
  }

  initSwipeable() {
    if (window.innerWidth > 767) {
      // Pointer drag to scroll
      let isDown = false;
      let startX;
      let startScroll;

      this.addEventListener("pointerdown", (e) => {
        isDown = true;
        // this.setPointerCapture(e.pointerId);
        this.classList.add("dragging");
        startX = e.clientX;
        startScroll = this.scrollLeft;
        // Prevent text selection while dragging
        e.preventDefault();
      });

      this.addEventListener("pointermove", (e) => {
        if (!isDown) {
          return;
        }
        const dx = e.clientX - startX;
        this.scrollLeft = startScroll - dx;
      });

      function release(e) {
        if (!isDown) {
          return;
        }
        isDown = false;
        try {
          if (e && e.pointerId) {
            this.releasePointerCapture(e.pointerId);
          }
        } catch (_) {
        }
        this.classList.remove("dragging");
      }

      this.addEventListener("pointerup", release);
      this.addEventListener("pointercancel", release);
      this.addEventListener("pointerleave", release);

      // Keyboard navigation (left/right)
      this.addEventListener("keydown", (e) => {
        const step = this.clientWidth * 0.7; // how far arrow/page moves
        if (e.key === "ArrowRight") {
          this.scrollBy({left: step, behavior: "smooth"});
          e.preventDefault();
        } else if (e.key === "ArrowLeft") {
          this.scrollBy({left: -step, behavior: "smooth"});
          e.preventDefault();
        } else if (e.key === "Home") {
          this.scrollTo({left: 0, behavior: "smooth"});
          e.preventDefault();
        } else if (e.key === "End") {
          this.scrollTo({left: this.scrollWidth, behavior: "smooth"});
          e.preventDefault();
        }
      });

      // Optional: make clicking items not trigger drag selection while dragging
      this.addEventListener("click", (e) => {
        // If you want to prevent clicks after drag, check whether a drag happened.
      });
    }
  }

  initHideStickyAction() {
    window.addEventListener("scroll", () => {
      const sticky = this.closest(".sticky-container");
      if (sticky) {
        const container = sticky.parentElement;

        const stickyBottom = Math.ceil(sticky.getBoundingClientRect().bottom);
        const containerBottom = Math.ceil(
          container.getBoundingClientRect().bottom
        );

        if (stickyBottom === containerBottom) {
          const productsWrapper =
            this.parentEl.querySelector("slider-component ul") ??
            this.parentEl.querySelector("collection-product-grid ul");
          const shownProductCards = productsWrapper.querySelectorAll(
            "li.animate--slide-in:not(.animate--slide-out)"
          );
          if (shownProductCards.length > 0) {
            sticky.style.opacity = 0;
            sticky.style.visibility = "hidden";
          }
        } else {
          sticky.style.opacity = 1;
          sticky.style.visibility = "visible";
        }
      }
    });
  }

  initActiveFontTypes() {
    this.querySelectorAll(".font-type").forEach((fontTypeEl) => {
      if (fontTypeEl.classList.contains("active")) {
        this.selectedFontTypes.push(fontTypeEl.dataset.fontType);
      }
    });
  }

  updateActiveFontTypes() {
    this.querySelectorAll(".font-type").forEach((fontTypeEl) => {
      const checkbox = fontTypeEl.querySelector("input");
      fontTypeEl.classList.remove("active");
      if (checkbox) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event("input", {bubbles: true}));
      }

      if (this.selectedFontTypes.includes(fontTypeEl.dataset.fontType)) {
        fontTypeEl.classList.add("active");
        if (checkbox) {
          checkbox.checked = true;
          checkbox.dispatchEvent(new Event("input", {bubbles: true}));
        }
      }
    });
  }

  updateShownProductList() {
    if (!this.parentEl) {
      return;
    }


    const productsWrapper =
      this.parentEl.querySelector("slider-component ul") ??
      this.parentEl.querySelector("collection-product-grid ul");

    productsWrapper.querySelectorAll("li").forEach((el) => {
      const cardEl = el.querySelector(".card");
      cardEl.style.borderRight = "0";

      if (this.selectedFontTypes[0] === "All") {
        el.classList.remove("animate--slide-out");
        setTimeout(() => {
          el.style.removeProperty("display");
        }, 300);
        return;
      }
      console.log(el);
      const elFontTypes = el.dataset.fontTypes.split(",");

      el.classList.remove("animate--slide-out");

      const set1 = new Set(this.selectedFontTypes);
      if (elFontTypes.some((item) => set1.has(item))) {
        el.classList.remove("animate--slide-out");
        setTimeout(() => {
          el.style.removeProperty("display");
        }, 300);
      } else {
        el.classList.add("animate--slide-out");
        setTimeout(() => {
          el.style.display = "none";
        }, 300);
      }
    });

    productsWrapper
      .querySelectorAll("li:not(.animate--slide-out")
      .forEach((el, index) => {
        if (index % 2 === 0) {
          const cardEl = el.querySelector(".card");
          cardEl.style.borderRight = "1px solid";
        }
      });
  }
}

customElements.define("filter-font-types", FilterFontTypes);

class PreviewTextInput extends HTMLElement {
  constructor() {
    super();

    this.updateHeight();
    window.addEventListener("resize", this.updateHeight.bind(this));

    this.parentEl = document.getElementById(this.dataset.parentId);
    this.input = this.querySelector("input");

    this.originalProductTitles = [];
    this.parentEl
      .querySelectorAll(".product-card-wrapper .product-title")
      .forEach((el, index) => {
        this.originalProductTitles[index] = el.innerHTML;
      });

    // this.input.addEventListener("change", this.onInputChange.bind(this));
    this.input.addEventListener("keyup", this.onInputChange.bind(this));
  }

  updateHeight() {
    const container = this.closest(".collection-product-grid-action-container");
    if (container) {
      container.style.height =
        document.querySelector("header").offsetHeight + "px";
    }
  }

  onInputChange(e) {
    const value = e.target.value;
    const productTitles = this.parentEl.querySelectorAll(
      ".product-card-wrapper .product-title"
    );

    if (value.replaceAll(" ", "") !== "") {
      productTitles.forEach((title) => {
        title.innerHTML = value;
      });
    } else {
      productTitles.forEach((title, index) => {
        title.innerHTML = this.originalProductTitles[index];
      });
    }
  }
}

customElements.define("preview-text-input", PreviewTextInput);

class FontSizeChanger extends HTMLElement {
  constructor() {
    super();

    this.parentEl = document.getElementById(this.dataset.parentId);
    this.slider = this.querySelector("input");
    this.slideValue = parseInt(this.slider.value ?? "100");

    this.init();

    window.addEventListener("text-scale-slider:reapply", () => {
      this.dispatchEvent(new Event("change", {bubbles: true}));
    });

    window.addEventListener("text-scale-slider:reset", () => {
      this.slider.value = 100;
    });
  }

  init() {
    this.addEventListener("input", (e) => this.onFontSizeSliderChange(e));
    this.addEventListener("change", (e) => this.onFontSizeSliderChange(e));
  }

  onFontSizeSliderChange(e) {

    this.slideValue = parseInt(this.slider.value ?? "100");

    const productsWrapper =
      this.parentEl.querySelector("slider-component ul") ??
      this.parentEl.querySelector("collection-product-grid ul");
    console.log(productsWrapper);
    productsWrapper.querySelectorAll(".card").forEach((el) => {
      // el.querySelector(".card__information").style.transform = `scale(${this.slideValue / 100})`;
      console.log('each');
      el.style.minHeight = "130px";

      const textEl = el.querySelector(".product-title");
      // const fontSize = parseFloat(
      //   getComputedStyle(textEl).getPropertyValue("font-size")
      // );
      const fontSize = parseFloat(textEl.dataset.fontSize);
      // console.log({ fontSize });

      let newFontSize = Math.floor((fontSize * this.slideValue) / 100);
      if (newFontSize < 10) {
        newFontSize = 10;
      }
      // console.log({ newFontSize });

      // textEl.setAttribute("data-font-size", newFontSize);
      textEl.style.fontSize = `${newFontSize}px`;
    });
    
  }
}

customElements.define("text-scale-slider", FontSizeChanger);

class ViewToggle extends HTMLElement {
  constructor() {
    super();

    this.parentEl = document.getElementById(this.dataset.parentId);
    this.buttons = this.querySelectorAll("button");
    // this.buttonGrid = this.querySelector("button#grid");

    // this.buttonList.addEventListener("click", this.onInputChange.bind(this));
    this.buttons.forEach((button) => {
      button.addEventListener("click", this.onButtonClick.bind(this));
    });
  }

  onButtonClick(e) {
    this.buttons.forEach((button) => {
      button.classList.remove("active");
    });

    e.target.classList.add("active");

    const productsWrapper =
      this.parentEl.querySelector("slider-component ul") ??
      this.parentEl.querySelector("collection-product-grid ul");


    gsap.to('.card-wrapper', {
      opacity: 0, scale: 0.8, duration: 0.25, onComplete: function() {
        productsWrapper.classList.remove("sm:grid-cols-1");
        productsWrapper.classList.remove("sm:grid-cols-2");
        productsWrapper.querySelectorAll("li .product-title").forEach((el) => {
          el.style.removeProperty("font-size");
        });
        if (e.target.id === "list") {
          productsWrapper.classList.add("sm:grid-cols-1");

          window.dispatchEvent(new Event("text-scale-slider:reset"));
          window.dispatchEvent(new Event("card-product:scale-product-title"));
        } else if (e.target.id === "grid") {
          productsWrapper.classList.add("sm:grid-cols-2");

          window.dispatchEvent(new Event("text-scale-slider:reset"));
          window.dispatchEvent(new Event("card-product:scale-product-title"));
        }
        gsap.to('.card-wrapper', {opacity: 1, scale: 1});
      }
    });


  }
}

customElements.define("view-toggle", ViewToggle);

class CardProduct extends HTMLElement {
  constructor() {
    super();
    // this.scaleText();

    window.addEventListener("resize", () => {
      console.log("resize");
      this.scaleText();
    });
    window.addEventListener("card-product:scale-product-title", () => {
      console.log("card-product:scale-product-title");

      this.scaleText();
    });

    document.fonts?.ready.then((e) => {
      // console.log("FONTS_READY");
      this.scaleText();
    });
  }

  scaleTextOld() {
    const slideEl = this.querySelector(".card__inner");
    const textEl = this.querySelector(".card__inner .product-title");

    if (slideEl && textEl) {
      textEl.style.fontSize = "10px";

      var computedStyle = getComputedStyle(slideEl);
      let slideElHeight = slideEl.clientHeight; // height with padding
      let slideElWidth = slideEl.clientWidth; // width with padding
      slideElHeight -=
        parseFloat(computedStyle.paddingTop) +
        parseFloat(computedStyle.paddingBottom);
      slideElWidth -=
        parseFloat(computedStyle.paddingLeft) +
        parseFloat(computedStyle.paddingRight);

      let textElHeight = textEl.clientHeight;
      let textElWidth = textEl.clientWidth;

      const scaleHeight = slideElHeight / textElHeight;
      const scaleWidth = slideElWidth / textElWidth;
      const scale = Math.min(scaleHeight, scaleWidth);

      const fontSize = parseFloat(
        getComputedStyle(textEl).getPropertyValue("font-size")
      );
      const newFontSize = Math.floor(fontSize * scale - 5);

      // textEl.style.transform = `scale(${scale})`;

      // textEl.setAttribute("data-font-size", newFontSize);
      textEl.style.fontSize = newFontSize + "px";
      textEl.style.opacity = "1";
      // console.log(newFontSize, "efrt");
    }
  }

  scaleText() {
    // alert('hey');
    const el = this.querySelector(".product-title");
    if (el.style.fontSize && el.style.fontSize != '4px') {
      // console.log("Inline font-size exists:", el.style.fontSize);
      return;
    } else {
      // console.log("No inline font-size");
    }

    const cardParentContainer = this.closest("ul");
    const cardMinHeight = parseFloat(cardParentContainer.dataset.cardMinHeight);
    const cardMinHeightMobile = parseFloat(
      cardParentContainer.dataset.cardMinHeightMobile
    );

    const cardEl = this.querySelector(".card");
    if (window.innerWidth > 767) {
      cardEl.style.minHeight = cardMinHeight + "px";
    } else {
      cardEl.style.minHeight = cardMinHeightMobile + "px";
    }

    const slideEl = this.querySelector(".card__inner");
    const textEl = this.querySelector(".card__inner .product-title");

    textEl.spellcheck = false;

    const scaleFontSizeUpTo = parseInt(slideEl.dataset.scaleFontSizeUpTo);

    if (slideEl && textEl) {
      /* OLD */
      // var computedStyle = getComputedStyle(slideEl);
      // let slideElWidth = slideEl.clientWidth; // width with padding
      // slideElWidth -=
      //   parseFloat(computedStyle.paddingLeft) +
      //   parseFloat(computedStyle.paddingRight);
      // const fontSize = parseFloat(
      //   getComputedStyle(textEl).getPropertyValue("font-size")
      // );
      // let textElWidth = textEl.clientWidth;
      // let scaleWidth = slideElWidth / textElWidth;
      // let newFontSize = Math.floor(fontSize * scaleWidth - 5);
      // if (newFontSize < 16) {
      //   newFontSize = 16;
      // }
      // textEl.style.fontSize = newFontSize + "px";
      /* OLD */

      /* NEW */
      let newFontSize = parseFloat(window.getComputedStyle(textEl).fontSize);
      // if (!textEl.classList.contains("edited")) {
      newFontSize = this.fitTextToParent(textEl, slideEl);
      // } else {
      //   return;
      // }
      /* NEW */

      textEl.style.opacity = "1";

      let maxTextSize = newFontSize + (newFontSize - 16);
      if (maxTextSize < 60) {
        maxTextSize = 60;
      }

      if (window.innerWidth > 767) {
        maxTextSize = newFontSize + scaleFontSizeUpTo;
      }
    }

    // window.dispatchEvent(
    //   new CustomEvent("text-scale-slider:reapply", { bubbles: true })
    // );
  }

  fitTextToParent(textEl, parentEl, min = 5, max = 100000) {
    // console.log("fitTextToParent");

    let low = min;
    let high = max;
    let best = min;

    const directParentEl = textEl.parentElement;
    directParentEl.style.width = "fit-content";

    textEl.style.width = "fit-content";
    textEl.style.fontSize = 10 + "px";
    textEl.style.whiteSpace = "nowrap";
    textEl.style.display = "inline-block";

    const computedParent = getComputedStyle(parentEl);
    const parentPaddingLeft = parseFloat(computedParent.paddingLeft) || 0;
    const parentPaddingRight = parseFloat(computedParent.paddingRight) || 0;

    const initialParentHeight = parentEl.clientHeight;
    const parentWidth =
      parentEl.clientWidth - parentPaddingLeft - parentPaddingRight;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      textEl.style.fontSize = `${mid}px`;

      let fits =
        textEl.scrollWidth <= parentWidth &&
        parentEl.clientHeight <= initialParentHeight;

      if (fits) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    best -= 1;

    textEl.setAttribute("data-font-size", best);
    textEl.style.fontSize = `${best}px`;

    textEl.style.removeProperty("white-space");

    directParentEl.style.width = "100%";
    textEl.style.width = "100%";

    return best;
  }
}

customElements.define("card-product", CardProduct);

class SampleText extends HTMLElement {
  constructor() {
    super();

    // this.scaleSampleText();

    document.addEventListener("click", (e) => {
      const container =
        e.target.classList.contains("font-weight") ||
        e.target.closest(".font-weight") !== null;
      if (!container) {
        this.hideAllFontWeightDropdown();
      }
    });

    window.addEventListener("resize", () => {
      this.scaleSampleText();
    });

    document.fonts?.ready.then(() => {
      this.scaleSampleText();
    });

    this.init();
    this.initSampleTextSettings();
  }

  init() {
    const slideEls = this.querySelectorAll(".sample-text-wrapper");
    slideEls.forEach((slideEl) => {
      const textEl = slideEl.querySelector(".block__sample-text");

      if (textEl) {
        textEl.addEventListener("click", () => {
          if (!textEl.classList.contains("edited")) {
            textEl.classList.add("edited");
          }
        });

        // textEl.addEventListener("input", () => {
        //   const span = textEl.querySelector("span");
        //   // If Safari inserted text outside the span, merge it back
        //   if (
        //     textEl.lastChild &&
        //     textEl.lastChild.nodeType === Node.TEXT_NODE &&
        //     textEl.lastChild !== span.firstChild
        //   ) {
        //     span.textContent += textEl.lastChild.textContent;
        //     textEl.removeChild(textEl.lastChild);
        //   }
        // });
      }
    });
  }

  onFontWeightChange(e, parentEl, fontWeightEl) {
    e.preventDefault();

    fontWeightEl.querySelector(".dropbtn .font-weight-name").innerHTML =
      e.target.innerHTML;
    const textEl = parentEl.querySelector(".block__sample-text");
    console.log(e.target.dataset);
    // textEl.style.fontWeight = parseInt(e.target.dataset.fontWeight);
    // textEl.style.fontStyle = e.target.dataset.fontStyle;
    textEl.style.fontFamily = e.target.dataset.fontFamily;
  }

  onFontSizeSliderChange(e, parentEl, fontSizeEl) {
    const textEl = parentEl.querySelector(".block__sample-text");
    textEl.style.fontSize = e.target.value + "px";

    fontSizeEl.querySelector(".showcase").innerHTML = e.target.value + "px";
    ScrollTrigger.refresh();
  }

  onLetterSpacingSliderChange(e, parentEl, letterSpacingEl) {
    const textEl = parentEl.querySelector(".block__sample-text");
    textEl.style.letterSpacing = e.target.value + "em";

    letterSpacingEl.querySelector(".showcase").innerHTML = e.target.value;
  }

  onLineHeightSliderChange(e, parentEl, lineHeightEl) {
    const textEl = parentEl.querySelector(".block__sample-text");
    textEl.style.lineHeight = e.target.value;

    lineHeightEl.querySelector(".showcase").innerHTML = e.target.value;
  }

  showFontWeightDropdown(dropdown) {
    const $clone = $(dropdown.querySelector(".dropdown-content")).clone(true); // "true" copies events

    const pos = this.getPositionRelativeToBody(dropdown);

    $clone.css({
      display: "block",
      position: "absolute",
      left: pos.x + "px", // X relative to document
      top: pos.y + pos.height + "px", // Y relative to document
    });

    $("body").append($clone);
  }

  hideAllFontWeightDropdown() {
    const slideEls = this.querySelectorAll(".sample-text-wrapper");
    slideEls.forEach((slideEl) => {
      const fontWeightEl = slideEl.querySelector(".settings .font-weight");
      const dropdown = fontWeightEl.querySelector(".dropdown");
      if (dropdown) {
        dropdown.classList.remove("show");
      }
    });

    $("body > .dropdown-content-font-weight").remove();
  }

  getPositionRelativeToBody(el) {
    const rect = el.getBoundingClientRect();
    const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    console.log(window.scrollY);
    console.log(window.pageYOffset);

    return {
      x: rect.left + scrollLeft,
      y: rect.top + scrollTop,
      height: rect.height,
    };
  }

  initSampleTextSettings() {
    const slideEls = this.querySelectorAll(".sample-text-wrapper");

    slideEls.forEach((slideEl) => {
      const fontWeightEl = slideEl.querySelector(".settings .font-weight");
      const fontSizeEl = slideEl.querySelector(".settings .font-size");
      const letterSpacingEl = slideEl.querySelector(
        ".settings .letter-spacing"
      );
      const lineHeightEl = slideEl.querySelector(".settings .line-height");

      const dropdown = fontWeightEl.querySelector(".dropdown");
      const dropbtn = fontWeightEl.querySelector(".dropbtn");

      dropdown?.querySelectorAll(".dropdown-content a").forEach((aEl) => {
        // using javascript
        // aEl.addEventListener("click", (e) =>
        //   this.onFontWeightChange(e, slideEl, fontWeightEl)
        // );
        // using jquery so when cloning the dropdown the events will also get cloned
        $(aEl).on("click", (e) =>
          this.onFontWeightChange(e, slideEl, fontWeightEl)
        );
      });

      if (dropdown && dropbtn) {
        dropbtn.addEventListener("click", () => {
          if (dropdown.classList.contains("show")) {
            this.hideAllFontWeightDropdown();
          } else {
            this.hideAllFontWeightDropdown();
            dropdown.classList.add("show");

            this.showFontWeightDropdown(dropdown);
          }
        });
      }

      fontSizeEl.addEventListener("input", (e) =>
        this.onFontSizeSliderChange(e, slideEl, fontSizeEl)
      );
      fontSizeEl.addEventListener("change", (e) =>
        this.onFontSizeSliderChange(e, slideEl, fontSizeEl)
      );

      letterSpacingEl.addEventListener("input", (e) =>
        this.onLetterSpacingSliderChange(e, slideEl, letterSpacingEl)
      );
      letterSpacingEl.addEventListener("change", (e) =>
        this.onLetterSpacingSliderChange(e, slideEl, letterSpacingEl)
      );

      lineHeightEl.addEventListener("input", (e) =>
        this.onLineHeightSliderChange(e, slideEl, lineHeightEl)
      );
      lineHeightEl.addEventListener("change", (e) =>
        this.onLineHeightSliderChange(e, slideEl, lineHeightEl)
      );
    });
  }

  scaleSampleText() {
    const slideEls = this.querySelectorAll(".sample-text-wrapper");

    slideEls.forEach((slideEl) => {
      const textEl = slideEl.querySelector(".block__sample-text");
      textEl.spellcheck = false;

      const scaleFontSizeUpTo = parseInt(slideEl.dataset.scaleFontSizeUpTo);
      const scaleFontSizeDownTo = parseInt(slideEl.dataset.scaleFontSizeDownTo);
// console.log(scaleFontSizeUpTo);
      if (slideEl && textEl) {
        const isMultiWord = slideEl.dataset.multiWord === "true";
        if (isMultiWord) {
          let initTextSize = parseInt(slideEl.dataset.textSizeDesktop);
          if (window.innerWidth <= 767) {
            initTextSize = parseInt(slideEl.dataset.textSizeMobile);
          }

          textEl.style.opacity = "1";

          let maxTextSize = initTextSize * 2 - 15;
          if (maxTextSize < 60) {
            maxTextSize = 60;
          }

          if (window.innerWidth > 767) {
            // maxTextSize = initTextSize + scaleFontSizeUpTo;
            maxTextSize = scaleFontSizeUpTo;
          }

          let minTextSize = scaleFontSizeDownTo;
          // console.log(maxTextSize);

          const fontSizeEl = slideEl.querySelector(".font-size");
          fontSizeEl.querySelector("span.showcase").innerHTML =
            initTextSize + "px";
          fontSizeEl.querySelector("input").setAttribute("max", maxTextSize);
          fontSizeEl.querySelector("input").setAttribute("min", minTextSize);
          fontSizeEl.querySelector("input").value = initTextSize;
          return;
        }

        /* OLD */
        // var computedStyle = getComputedStyle(slideEl);
        // let slideElWidth = slideEl.clientWidth; // width with padding
        // slideElWidth -=
        //   parseFloat(computedStyle.paddingLeft) +
        //   parseFloat(computedStyle.paddingRight);
        // const fontSize = parseFloat(
        //   getComputedStyle(textEl).getPropertyValue("font-size")
        // );
        // let textElWidth = textEl.clientWidth;
        // let scaleWidth = slideElWidth / textElWidth;
        // let newFontSize = Math.floor(fontSize * scaleWidth - 5);
        // if (newFontSize < 16) {
        //   newFontSize = 16;
        // }
        // textEl.style.fontSize = newFontSize + "px";
        /* OLD */

        /* NEW */
        let newFontSize = parseFloat(window.getComputedStyle(textEl).fontSize);
        // if (!textEl.classList.contains("edited")) {
        newFontSize = this.fitTextToParent(textEl, slideEl);
        // } else {
        //   return;
        // }
        /* NEW */

        textEl.style.opacity = "1";

        let maxTextSize = newFontSize + (newFontSize - 16);
        if (maxTextSize < 60) {
          maxTextSize = 60;
        }

        if (window.innerWidth > 767) {
          maxTextSize = newFontSize + scaleFontSizeUpTo;
        }

        // console.log(maxTextSize);

        const fontSizeEl = slideEl.querySelector(".font-size");
        fontSizeEl.querySelector("span.showcase").innerHTML =
          newFontSize + "px";
        fontSizeEl.querySelector("input").setAttribute("max", maxTextSize);
        fontSizeEl.querySelector("input").value = newFontSize;
      }
    });
    
  }

  fitTextToParent(textEl, parentEl, min = 5, max = 100000) {
    
    let low = min;
    let high = max;
    let best = min;

    textEl.style.width = "fit-content";
    textEl.style.whiteSpace = "nowrap";
    textEl.style.display = "inline-block";

    const computedParent = getComputedStyle(parentEl);
    const parentPaddingLeft = parseFloat(computedParent.paddingLeft) || 0;
    const parentPaddingRight = parseFloat(computedParent.paddingRight) || 0;

    const computedText = getComputedStyle(textEl);
    const textPaddingLeft = parseFloat(computedText.paddingLeft) || 0;
    const textPaddingRight = parseFloat(computedText.paddingRight) || 0;

    const parentWidth =
      parentEl.clientWidth - parentPaddingLeft - parentPaddingRight;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      textEl.style.fontSize = `${mid}px`;

      // let fits = textEl.scrollWidth <= parentWidth;
      let fits =
        // .querySelector("span")
        textEl.offsetWidth <=
        // +
        //   textPaddingLeft +
        //   textPaddingRight
        parentWidth;

      // if (isIOS()) {
      //   // fits = textEl.scrollWidth - mid * 0.25 <= parentWidth;
      //   fits = textEl.scrollWidth - mid * 0.2 <= parentWidth;
      // }

      if (fits) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    // best -= 5;

    textEl.style.fontSize = `${best}px`;

    textEl.style.removeProperty("white-space");

    return best;
  }
}

customElements.define("sample-text", SampleText);

class SpecialCharacteristics extends HTMLElement {
  constructor() {
    super();

    // this.scaleCharacters();

    // window.addEventListener("resize", () => {
    //   this.scaleCharacters();
    // });

    // document.fonts?.ready.then(() => {
    //   this.scaleCharacters();
    // });
  }

  scaleCharacters() {
    const slideEls = this.querySelectorAll(".character-wrapper .character");

    let newFontSize = 1000;

    slideEls.forEach((slideEl) => {
      const textEl = slideEl.querySelector("span");

      if (slideEl && textEl) {
        var computedStyle = getComputedStyle(slideEl);

        let slideElWidth = slideEl.clientWidth; // width with padding

        slideElWidth -=
          parseFloat(computedStyle.paddingLeft) +
          parseFloat(computedStyle.paddingRight);

        const fontSize = parseFloat(
          getComputedStyle(textEl).getPropertyValue("font-size")
        );

        let textElWidth = textEl.clientWidth;

        const scaleWidth = slideElWidth / textElWidth;

        const newFS = Math.floor(fontSize * scaleWidth);
        if (newFS < newFontSize) {
          newFontSize = newFS;
        }

        // textEl.style.fontSize = newFontSize + "px";
      }
    });

    slideEls.forEach((slideEl) => {
      const textEl = slideEl.querySelector("span");

      if (slideEl && textEl) {
        textEl.style.fontSize = newFontSize + "px";
      }
    });
  }
}

customElements.define("special-characteristics", SpecialCharacteristics);

class CharacterListWithMagnify extends HTMLElement {
  constructor() {
    super();

    this.wrapper = this.querySelector(".character-list-wrapper");
    this.magnifier = this.wrapper.querySelector(".magnifier");

    this.querySelectorAll(".character-list-wrapper .character").forEach(
      (character) => {
        character.addEventListener("mouseover", (event) => {
          this.magnifier.textContent = event.target.textContent;
        });

        character.addEventListener("mousemove", (event) => {
          const rect = this.wrapper.getBoundingClientRect();
          const magnifierRect = this.magnifier.getBoundingClientRect();

          let clientX = event.clientX;
          let clientY = event.clientY;

          const newClientPos = this.reposition(clientX, clientY);
          clientX = newClientPos.x;
          clientY = newClientPos.y;

          const x = clientX - rect.left;
          const y = clientY - rect.top;

          this.magnifier.style.transform = `translate(calc(${x}px - ${
            magnifierRect.width / 2
          }px),calc(${y}px - ${magnifierRect.height / 2}px))`;
        });
      }
    );
  }

  reposition(x, y) {
    const magnifierRect = this.magnifier.getBoundingClientRect();

    // Make sure magnifier appear inside viewport
    const xLeft = x - magnifierRect.width / 2;
    const xRight = x + magnifierRect.width / 2 + 48;
    const yTop = y - magnifierRect.height / 2;
    const yBottom = y + magnifierRect.height / 2;

    let newX = x;
    let newY = y;

    if (xLeft < 0) {
      newX += xLeft * -1;
    }
    if (xRight > window.innerWidth) {
      newX -= xRight - window.innerWidth;
    }
    if (yTop < 0) {
      newY += yTop * -1;
    }
    if (yBottom > window.innerHeight) {
      newY -= yBottom - window.innerHeight;
    }

    return {x: newX, y: newY};
  }
}

customElements.define("character-list-with-magnify", CharacterListWithMagnify);

class FeaturedImages extends HTMLElement {
  constructor() {
    super();

    this.columnsMobile = parseInt(this.dataset.columnsMobile);
    this.gridDesktopHorizontalSpacing = parseInt(
      this.dataset.gridDesktopHorizontalSpacing
    );
    this.gridMobileHorizontalSpacing = parseInt(
      this.dataset.gridMobileHorizontalSpacing
    );
    this.imageRatio = parseFloat(this.dataset.imageRatio);

    this.update();

    window.addEventListener("resize", this.update.bind(this));
  }

  update() {
    const items = this.querySelectorAll(
      ".collection-list .collection-list__item"
    );

    items.forEach((item) => {
      item.style.width = "";
      item.style.maxWidth = "";
      item.querySelector(".media-wrapper").style.paddingTop =
        100 * this.imageRatio + "%";
    });

    let columns = this.columnsDesktop;
    let spacing = this.gridDesktopHorizontalSpacing;
    if (window.innerWidth <= 767) {
      columns = this.columnsMobile;
      spacing = this.gridMobileHorizontalSpacing;
    }

    const lastRowItems = items.length % columns;
    if (lastRowItems !== 0) {
      for (let i = 1; i <= lastRowItems; i++) {
        const item = items[items.length - i];

        item.style.width = `calc(100% / ${lastRowItems} - ${spacing}px * ${
          lastRowItems - 1
        } / ${lastRowItems})`;
        item.style.maxWidth = `calc(100% / ${lastRowItems} - ${spacing}px * ${
          lastRowItems - 1
        } / ${lastRowItems})`;

        item.querySelector(".media-wrapper").style.paddingTop = `calc(100% * ${
          lastRowItems / columns
        })`;
      }
    }
  }
}

customElements.define("featured-images", FeaturedImages);

class CollapsibleContent extends HTMLElement {
  constructor() {
    super();

    this.detailsList = this.querySelectorAll("details");
    this.contentList = this.querySelectorAll(".accordion__content");
    this.contentListOriginalHeights = [];

    this.contentList.forEach((content) => {
      content.style.height = content.offsetHeight + "px";
      this.contentListOriginalHeights.push(content.offsetHeight);
    });

    this.detailsList.forEach((details, index) => {
      const defaultOpen = details.dataset.defaultOpen === "true";
      if (!defaultOpen) {
        details.open = false;
      }
    });

    this.contentList.forEach((content, index) => {
      content.nextElementSibling.addEventListener("click", (e) => {
        e.preventDefault();

        if (e.target.classList.contains("more")) {
          content.style.height = this.contentListOriginalHeights[index] + "px";
          e.target.classList.remove("more");
          e.target.classList.add("less");
          e.target.innerHTML = "Show less";
        } else {
          content.style.height = "50vh";
          e.target.classList.remove("less");
          e.target.classList.add("more");
          e.target.innerHTML = "Read more";
        }
      });

      if (content.offsetHeight > window.innerHeight / 2) {
        content.style.height = "50vh";
        content.nextElementSibling.classList.remove("hidden");
      }
    });
  }
}

customElements.define("collapsible-content", CollapsibleContent);

class CartItemPreviewText extends HTMLElement {
  constructor() {
    super();

    // this.scaleText();

    window.addEventListener("resize", this.scaleText.bind(this));

    document.fonts?.ready.then(() => this.scaleText());
  }

  scaleText() {
    const slideEl = this.querySelector(".text-wrapper");
    const textEl = this.querySelector(".text-wrapper span");

    if (slideEl && textEl) {
      var computedStyle = getComputedStyle(slideEl);

      let slideElHeight = slideEl.clientHeight; // height with padding
      let slideElWidth = slideEl.clientWidth; // width with padding
      slideElHeight -=
        parseFloat(computedStyle.paddingTop) +
        parseFloat(computedStyle.paddingBottom);
      slideElWidth -=
        parseFloat(computedStyle.paddingLeft) +
        parseFloat(computedStyle.paddingRight);

      let textElHeight = textEl.clientHeight;
      let textElWidth = textEl.clientWidth;

      const scaleHeight = slideElHeight / textElHeight;
      const scaleWidth = slideElWidth / textElWidth;

      const scale = Math.min(scaleHeight, scaleWidth);

      const fontSize = parseFloat(
        getComputedStyle(textEl).getPropertyValue("font-size")
      );
      const newFontSize = Math.floor(fontSize * scale - 5);

      // textEl.style.transform = `scale(${scale})`;
      textEl.style.fontSize = newFontSize + "px";
    }
  }
}

customElements.define("cart-item-preview-text", CartItemPreviewText);

class CustomPage extends HTMLElement {
  constructor() {
    super();

    if (window.location.hash && window.location.hash !== "#") {
      const target = window.location.hash
        .replace("#", "")
        .replaceAll("%20", " ");

      setTimeout(() => {
        this.scrollTextElementIntoView(target);
      }, 200);
    }

    this.init();

    this.sidebarMenuDropdowns = this.querySelectorAll(
      ".sidebar-menu .dropdown"
    );

    this.initSidebarMenuDropdown();

    // window.addEventListener("resize", this.initSidebarMenuDropdown.bind(this));
  }

  init() {


    document.querySelectorAll('.js--hero').forEach(function(element) {
      const cloneElement = element.cloneNode(true);
      document.querySelector(".main-banner").appendChild(cloneElement);
      element.remove();
    })

    const pageHero = this.querySelector(".page-hero");
    if (pageHero) {
      const clonePageHero = pageHero.cloneNode(true);
      this.querySelector(".main-banner").appendChild(clonePageHero);
      pageHero.remove();
    }

    const sidebarMenus = this.querySelectorAll(".page-sidebar-menu");
    if (sidebarMenus.length > 0) {
      sidebarMenus.forEach((menu) => {
        let cloneMenu = menu.querySelector(".content").cloneNode(true);
        cloneMenu.removeAttribute("aria-hidden");
        this.querySelector(".sidebar-menu").appendChild(cloneMenu);

        menu.remove();
      });

      const menuItems = this.querySelectorAll("[data-target]");
      menuItems.forEach((item) => {
        item.addEventListener("click", () => {
          // e.preventDefault();
          this.scrollTextElementIntoView(item.dataset.target);
        });
      });
    }
  }

  closeAllDropdowns() {
    this.sidebarMenuDropdowns.forEach((dropdown) => {
      dropdown.classList.remove("show");
    });
  }

  initSidebarMenuDropdown() {
    // if (window.innerWidth <= 767) {
    this.sidebarMenuDropdowns.forEach((dropdown) => {
      dropdown.addEventListener("click", () => {
        if (dropdown.classList.contains("show")) {
          this.closeAllDropdowns();
          // dropdown.classList.remove("show");
        } else {
          this.closeAllDropdowns();
          dropdown.classList.add("show");
        }
      });
    });
    // }
  }

  scrollTextElementIntoView(textEl) {
    const elements = this.querySelectorAll(".main-content *");

    // Iterate over all elements and find the one with the desired text
    var foundElement = Array.from(elements).find(function(element) {
      return (
        element.textContent.trim().toLowerCase() === textEl.trim().toLowerCase()
      );
    });

    if (foundElement) {
      foundElement.closest("section").style.scrollMarginTop = "80px";
      foundElement.closest("section").scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
    }
  }
}

customElements.define("custom-page", CustomPage);

class MarqueeText extends HTMLElement {
  constructor() {
    super();

    this.init();

    window.addEventListener("resize", this.init.bind(this));
  }

  init() {
    const marqueeContent = this.querySelector(".marquee-content");
    const marqueeContentClone = marqueeContent.cloneNode(true);
    let multiplier =
      Math.ceil((window.innerWidth / marqueeContent.clientWidth) * 2) - 1;
    if (multiplier <= 1) {
      multiplier = 3;
    }

    this.innerHTML = "";

    for (let i = 0; i < multiplier; i++) {
      this.appendChild(marqueeContentClone.cloneNode(true));
    }
  }
}

customElements.define("marquee-text", MarqueeText);

window.addEventListener("DOMContentLoaded", () => {
  // Fix custom ul & ol symbol position & size
  const rteUlLiEls = document.querySelectorAll(".rte ul li");
  rteUlLiEls.forEach((li) => {
    const firstChild = li.firstElementChild;

    let paddingLeftValue =
      parseFloat(window.getComputedStyle(li).fontSize) * 1.25;
    // let topValue = parseFloat(window.getComputedStyle(li).fontSize) * 0.25;
    let sizeValue = parseFloat(window.getComputedStyle(li).fontSize) * 0.75;
    let lineHeightValue = parseFloat(window.getComputedStyle(li).lineHeight);

    if (firstChild) {
      paddingLeftValue =
        parseFloat(window.getComputedStyle(firstChild).fontSize) * 1.25;
      // topValue =
      //   parseFloat(window.getComputedStyle(firstChild).fontSize) * 0.25;
      sizeValue =
        parseFloat(window.getComputedStyle(firstChild).fontSize) * 0.75;
      lineHeightValue = parseFloat(
        window.getComputedStyle(firstChild).lineHeight
      );
    }

    let topValue = (lineHeightValue - sizeValue) / 2;

    li.style.setProperty("--before-padding-left", paddingLeftValue + "px");
    li.style.setProperty("--before-top", topValue + "px");
    li.style.setProperty("--before-size", sizeValue + "px");
  });

  const rteOlLiEls = document.querySelectorAll(".rte ol li");
  rteOlLiEls.forEach((li) => {
    const firstChild = li.firstElementChild;

    let paddingLeftValue =
      parseFloat(window.getComputedStyle(li).fontSize) * 1.25;
    let sizeValue = parseFloat(window.getComputedStyle(li).fontSize);
    let lineHeightValue = parseFloat(window.getComputedStyle(li).lineHeight);

    if (firstChild) {
      paddingLeftValue =
        parseFloat(window.getComputedStyle(firstChild).fontSize) * 1.25;
      sizeValue = parseFloat(window.getComputedStyle(firstChild).fontSize);
      lineHeightValue = parseFloat(
        window.getComputedStyle(firstChild).lineHeight
      );
    }

    li.style.setProperty("--before-padding-left", paddingLeftValue + "px");
    li.style.setProperty("--before-size", sizeValue + "px");
    li.style.setProperty("--before-line-height", lineHeightValue + "px");
  });
});
